import {
  BUTTON_LAYERS_CONFIG,
  CIRCLE_ICON,
  LINE_ICON,
  RECTANGLE_ICON,
} from '@App/app/configs/button-layers.config';
import { EDIT_MODE } from '@App/app/configs/hints.config';
import { EDIT_INIT_TOOLS } from '@App/app/configs/layers-bar.config';
import { CustomerEngineService } from '@App/app/engine/services/engine-services/customer-engine-service/customer-engine.service';
import { LayerEventsService } from '@App/app/engine/services/layer-services/layer-events-service/layer-events.service';
import { ViewerLayerService } from '@App/app/engine/services/layer-services/viewer-layer-service/viewer-layer.service';
import { UserPermissions } from '@App/app/entities/auth/permissions.enum';
import { LAYER_NAMES } from '@App/app/entities/layer/enums/layer-names.enum';
import { LAYER_EVENTS } from '@App/app/entities/layer/enums/layer-types.enum';
import { SAVE_SKETCH_TYPE } from '@App/app/entities/layer/sketch-tools/save-sketch-types.enum';
import { EVENT_TYPE } from '@App/app/entities/shared/event-types.enum';
import { LayerButtonFunc } from '@App/app/entities/viewer/layer-button.model';
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { NbDialogService, NbPopoverDirective } from '@nebular/theme';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions } from '@ngrx/effects';
import { Observable } from 'rxjs';
import { LayersService } from '@App/app/pages/viewer/services/layers/layers.service';
import { BroadcastService } from '@App/app/shared/broadcast.service';
import { RendersService } from '../../models/services/renders-service/renders.service';
import { CreateLayerButtonService } from '../create-layer-button/create-layer-button.service';
import { ActiveLayerToolService } from '../services/active-layer-tool/active-layer-tool.service';
import { AutomappingService } from '../services/automapping/automapping.service';
import { TutorialVideoPopupComponent } from '../tutorials-video/tutorial-video-popup/tutorial-video-popup.component';
import { HintsService } from './../hints/hints.service';
import { CreateLayerBarService } from './create-layer-bar.service';
import { initCreateLayersBarSubscriptions } from './create-layers-bar.subscriptions';

@UntilDestroy()
@Component({
  selector: 'app-create-layers-bar',
  templateUrl: './create-layers-bar.component.html',
  styleUrls: ['./create-layers-bar.component.scss'],
})
export class CreateLayersBarComponent implements OnInit, OnDestroy {
  @ViewChildren(NbPopoverDirective) popovers: QueryList<NbPopoverDirective>;
  editInitTools = EDIT_INIT_TOOLS;
  LAYER_NAMES = LAYER_NAMES;
  LAYER_EVENTS = LAYER_EVENTS;
  activeTool: LAYER_NAMES = LAYER_NAMES.NULL;
  activeEvent: LAYER_EVENTS = LAYER_EVENTS.NULL;
  isBasePlaneCreated = true;
  isEditMode: boolean;
  buttonsDisabled = true;
  sketchToSave: SAVE_SKETCH_TYPE;
  isModelLoaded = false;
  layersLoaded = false;
  permissions = UserPermissions;
  isPlaneEditing$: Observable<boolean>;
  hintsActive$: Observable<boolean>;
  currentHint$: Observable<string | null>;
  actualPopoverIndex: number;
  circleIcon = CIRCLE_ICON;
  lineIcon = LINE_ICON;
  rectangleIcon = RECTANGLE_ICON;
  EDIT_MODE = EDIT_MODE;
  BUTTON_LAYERS: LayerButtonFunc[] = [];

  constructor(
    public broadcastService: BroadcastService,
    private engineService: CustomerEngineService,
    public hintsService: HintsService,
    public layerEventsService: LayerEventsService,
    private layersService: LayersService,
    private viewerLayerService: ViewerLayerService,
    public automappingService: AutomappingService,
    public dialogService: NbDialogService,
    public activeLayerToolService: ActiveLayerToolService,
    public createLayerBarService: CreateLayerBarService,
    public rendersService: RendersService,
    private cdr: ChangeDetectorRef,
    private createLayerButtonService: CreateLayerButtonService,
    public actions$: Actions,
  ) {
    initCreateLayersBarSubscriptions(this);
  }

  ngOnInit(): void {
    this.viewerLayerService.layersLoaded$.pipe(untilDestroyed(this)).subscribe((value) => {
      this.layersLoaded = value;
    });
    this.engineService.isModelLoaded$.pipe(untilDestroyed(this)).subscribe((value) => {
      this.isModelLoaded = value;
    });
    this.layersService.isBasePlaneCreated$.pipe(untilDestroyed(this)).subscribe((value) => {
      this.isBasePlaneCreated = value;
    });
    this.BUTTON_LAYERS = BUTTON_LAYERS_CONFIG.map((config) => {
      return {
        ...config,
        func: this.getFunctionForTool(config.toolName),
      };
    });
  }

  ngOnDestroy() {
    this.activeLayerToolService.setActiveTool();
  }

  generateEditMode(eventType: string) {
    this.broadcastService
      .on(eventType)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.generateManualTool();
      });
  }

  toggleActiveLayer(event: LAYER_EVENTS, name: LAYER_NAMES) {
    this.createLayerButtonService.toggleActiveLayer(event, name);
    this.showToolInfo();
  }

  showToolInfo() {
    if (this.activeTool === LAYER_NAMES.NULL) {
      this.cdr.detectChanges();
      this.hidePopover();
    }
  }

  toggleTakeBasePlane() {
    this.broadcastService.broadcast(EVENT_TYPE.TAKE_BASE_PLANE, null);
  }

  generateManualTool() {
    this.createLayerButtonService.generateManualTool();
  }

  toggleVideo() {
    this.dialogService.open(TutorialVideoPopupComponent, {
      context: { toolEvent: this.activeEvent },
    });
  }

  onSaveClick() {
    this.broadcastService.broadcast(this.sketchToSave, null);
    this.buttonsDisabled = true;
    this.broadcastService.broadcast(EVENT_TYPE.CAMERA_MODE_BUTTON_STATUS, false);
  }

  onCancelClick() {
    this.buttonsDisabled = true;
    this.broadcastService.broadcast(EVENT_TYPE.TAKE_SKETCH_PLANE, false);
    this.broadcastService.broadcast(EVENT_TYPE.CAMERA_MODE_BUTTON_STATUS, false);
  }

  createNewFolder() {
    this.createLayerBarService.createNewFolder();
  }

  clear() {
    this.activeLayerToolService.setActiveTool();
    this.layerEventsService.activeLayerEvent.next(LAYER_EVENTS.NULL);
  }

  hidePopover() {
    if (this.actualPopoverIndex >= 0) {
      this.popovers.get(this.actualPopoverIndex)?.hide();
    }
  }

  getFunctionForTool(toolName: string) {
    return this.createLayerButtonService.buttonFunctions[toolName] ?? (() => {});
  }
}
